import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Input } from './ui/input';
import themesData from '../data/themes.json';
import postsData from '../data/posts.json';
import coursesData from '../data/courses.json';

interface SearchResult {
  id: string;
  type: 'theme' | 'post' | 'course';
  title: string;
  description: string;
  url: string;
  thumbnail?: string;
}

interface GlobalSearchProps {
  size?: 'default' | 'large';
  className?: string;
  placeholder?: string;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  size = 'default',
  className = '',
  placeholder = 'Search themes, courses, posts...',
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const searchQuery = query.toLowerCase();
    const allResults: SearchResult[] = [];

    // Search themes
    themesData.forEach((theme: any) => {
      const matchTitle = theme.title.toLowerCase().includes(searchQuery);
      const matchTags = theme.tags?.some((tag: string) => tag.toLowerCase().includes(searchQuery));
      const matchCategory = theme.category.toLowerCase().includes(searchQuery);
      
      if (matchTitle || matchTags || matchCategory) {
        allResults.push({
          id: theme.id,
          type: 'theme',
          title: theme.title,
          description: theme.description,
          url: `/theme/${theme.slug}`,
          thumbnail: theme.thumbnail,
        });
      }
    });

    // Search posts
    postsData.forEach((post: any) => {
      const matchTitle = post.title.toLowerCase().includes(searchQuery);
      const matchTags = post.tags?.some((tag: string) => tag.toLowerCase().includes(searchQuery));
      const matchCategory = post.category.toLowerCase().includes(searchQuery);
      
      if (matchTitle || matchTags || matchCategory) {
        allResults.push({
          id: post.id,
          type: 'post',
          title: post.title,
          description: post.excerpt,
          url: `/post/${post.slug}`,
          thumbnail: post.featuredImage,
        });
      }
    });

    // Search courses
    coursesData.forEach((course: any) => {
      const matchTitle = course.title.toLowerCase().includes(searchQuery);
      const matchDescription = course.description.toLowerCase().includes(searchQuery);
      
      if (matchTitle || matchDescription) {
        allResults.push({
          id: course.id,
          type: 'course',
          title: course.title,
          description: course.description,
          url: `/course/${course.slug}`,
          thumbnail: course.thumbnail,
        });
      }
    });

    setResults(allResults.slice(0, 8));
    setIsOpen(true);
  }, [query]);

  const sizeClasses = size === 'large' ? 'text-lg py-4 px-6 pl-12' : '';

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          className={`pl-12 pr-12 ${sizeClasses} bg-white border-gray-200 focus:border-sky-500 transition-all shadow-sm`}
        />
        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        {query && (
          <button
            onClick={() => {
              setQuery('');
              setResults([]);
              setIsOpen(false);
            }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 max-h-96 overflow-y-auto z-50">
          <div className="p-2">
            {results.map((result) => (
              <Link
                key={`${result.type}-${result.id}`}
                to={result.url}
                onClick={() => {
                  setQuery('');
                  setIsOpen(false);
                }}
                className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors group"
              >
                {result.thumbnail && (
                  <img
                    src={result.thumbnail}
                    alt={result.title}
                    className="w-12 h-12 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900 group-hover:text-sky-500 transition-colors truncate">
                      {result.title}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full flex-shrink-0">
                      {result.type}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {result.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {isOpen && query.length >= 2 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 p-4 z-50">
          <p className="text-sm text-gray-500 text-center">No results found for "{query}"</p>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;